import { z } from "zod";
import { IsoDateTimeSchema } from "./common.js";
/**
 * A licença do jogo (`POST /v1/license`).
 *
 * O jogo é comprado, com teste grátis de sete dias. A simulação roda no
 * aparelho e segue sem internet, então a trava não pode depender de o servidor
 * responder a cada rodada: o servidor entrega ao aparelho um **bilhete
 * assinado** dizendo até quando ele pode jogar, e o jogo confere a assinatura
 * com a chave pública que vem dentro dele. Fabricar um bilhete exigiria a chave
 * privada, que só o servidor tem.
 *
 * O bilhete é `<dados em base64url>.<assinatura em base64url>`, ECDSA P-256 com
 * SHA-256, assinatura no formato IEEE P1363 (r‖s), que é o que a WebCrypto do
 * navegador confere sem biblioteca nenhuma. A assinatura cobre os bytes ASCII
 * do primeiro trecho, como num JWS.
 */
/** Duração do teste grátis, contada a partir da primeira vez que a pessoa joga. */
export const LICENSE_TRIAL_DAYS = 7;
/** Aparelhos ativos ao mesmo tempo por conta. O navegador não conta. */
export const LICENSE_MAX_DEVICES = 3;
/** Ativações de aparelho aceitas numa janela de trinta dias. */
export const LICENSE_MAX_ACTIVATIONS_30D = 6;
/** Quanto tempo um bilhete de licença vale sem o aparelho voltar a falar com o servidor. */
export const LICENSE_OFFLINE_DAYS = 14;
/**
 * Situação da conta.
 *
 * - `licensed`: comprou (licença vitalícia ou Passe ativo), ou é da equipe.
 * - `trial`: dentro dos sete dias.
 * - `trial_available`: ainda não jogou; o teste começa quando pedir.
 * - `trial_used`: este aparelho já gastou o teste em outra conta.
 * - `expired`: o teste acabou e não há compra.
 */
export const LicenseStatusSchema = z.enum([
    "licensed",
    "trial",
    "trial_available",
    "trial_used",
    "expired",
]);
/**
 * Identificador do aparelho: sorteado por ele na primeira abertura e guardado
 * nele. Não é segredo nem prova nada sozinho; serve para contar aparelhos.
 */
export const DeviceIdSchema = z.string().regex(/^[A-Za-z0-9_-]{16,64}$/, "Identificador de aparelho inválido.");
export const LicenseRequestSchema = z
    .object({
    deviceId: DeviceIdSchema,
    /** Começa o teste, se ele ainda não começou. O jogo manda ao abrir uma carreira. */
    startTrial: z.boolean().optional(),
})
    .strict();
export const LicenseResponseSchema = z.object({
    status: LicenseStatusSchema,
    /** Fim do teste, quando ele já começou. */
    trialEndsAt: IsoDateTimeSchema.nullable(),
    /** Bilhete assinado para este aparelho. Nulo quando não há o que liberar. */
    ticket: z.string().nullable(),
    ticketExpiresAt: IsoDateTimeSchema.nullable(),
});
/** O que vai dentro do bilhete. Tempos em segundos desde a época, como num JWT. */
export const LicenseTicketPayloadSchema = z.object({
    v: z.literal(1),
    /** Conta dona do bilhete. */
    sub: z.string(),
    /** Aparelho para o qual o bilhete foi emitido. */
    dev: DeviceIdSchema,
    st: z.enum(["licensed", "trial"]),
    iat: z.number().int(),
    exp: z.number().int(),
});
//# sourceMappingURL=license.js.map